import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import {NavBar} from "@/components/navbar";
import {Footer} from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import ComplianceAIForm from "@/components/compliance-ai/ComplianceAIForm";
import ComplianceAIResponse from "@/components/compliance-ai/ComplianceAIResponse";
import { ComplianceFormData } from "@/types/compliance";
import { mockComplianceAiResponse } from "@/mocks/complianceAiResponse";
import { API_CONFIG } from "@/constants/env.ts";

const AIFMDLanding22 = () => {
  const [formData, setFormData] = useState<ComplianceFormData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [response, setResponse] = useState<any | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(60); // For loading UI if needed

  const handleFormSubmit = async (data: ComplianceFormData) => {
    setFormData(data);
    setIsLoading(true);
    setError(null);

    try {
      // Use the actual API instead of mock data
      const apiUrl = `${window.location.origin}/compliance-ai`;
      console.log("Sending request to:", apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}: ${response.statusText}`);
      }
      
      let responseData = await response.json();
      
      // Log the response for debugging
      console.log("API Response:", responseData);
      
      // Process the response if needed (similar to ComplianceAI.tsx)
      if (typeof responseData === 'string') {
        try {
          responseData = JSON.parse(responseData);
        } catch (e) {
          responseData = { content: responseData };
        }
      }
      
      setResponse(responseData);
    } catch (error) {
      console.error('Error submitting form:', error);
      
      if (error instanceof Error) {
        console.log('Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
        setError(error);
      } else {
        setError(new Error('An unknown error occurred'));
      }
      
      // Fallback to mock data if API fails
      if (API_CONFIG.USE_MOCK_API) {
        console.log("Falling back to mock data due to API error");
        setTimeout(() => {
          setResponse({
            ...mockComplianceAiResponse,
            title: "AIFMD Marketing Compliance Assessment",
            summary: "Your financial marketing materials have been analyzed for AIFMD compliance. Here are our recommendations to improve your marketing compliance posture."
          });
        }, 1000);
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="relative min-h-screen overflow-hidden">
      <Helmet>
        <title>AI-Powered AIFMD Compliance Analysis | Verdea AI</title>
        <meta 
          name="description" 
          content="Get a free analysis of how AI can help with your AIFMD marketing compliance processes. Transform your compliance workflow with Verdea AI." 
        />
      </Helmet>
      
      {/* Background Elements */}
      <div className="fixed inset-0 z-0 pointer-events-none bg-gray-50">
        <div
          className="absolute top-0 right-0 w-[500px] h-[500px] blur-3xl opacity-20"
          style={{
            background: 'radial-gradient(circle at center, rgba(16, 185, 129, 0.4) 0%, rgba(16, 185, 129, 0) 70%)',
            transform: 'translate(30%, -30%)'
          }}
        ></div>
        <div
          className="absolute bottom-0 left-0 w-[500px] h-[500px] blur-3xl opacity-20"
          style={{
            background: 'radial-gradient(circle at center, rgba(16, 185, 129, 0.3) 0%, rgba(16, 185, 129, 0) 70%)',
            transform: 'translate(-30%, 30%)'
          }}
        ></div>
      </div>
      
      {/* Navbar - landing mode hides platform, tools and login buttons */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm">
        <NavBar landingMode={true} />
      </header>
      
      <main className="relative z-10 pt-20">
        {/* Main content with form section */}
        <section className="py-12 pb-16 bg-transparent">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto text-center mb-8">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 tracking-tight">
                How AI Can Transform Your AIFMD Compliance Processes
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
                Get a free detailed analysis of how artificial intelligence can streamline your 
                AIFMD marketing compliance workflow and save your team hours of manual work.
              </p>
              <div className="flex justify-center items-center gap-4 mb-6">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-emerald-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Free Analysis</span>
                </div>
                <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-emerald-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>No Registration Required</span>
                </div>
                <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-emerald-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Instant Results</span>
                </div>
              </div>
            </div>
            
            {/* Form Section */}
            <div className="max-w-5xl mx-auto mb-16">
              <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-200/80">
                {!response ? (
                  <>
                    <h2 className="text-2xl font-bold mb-6 text-center">Tell us about your AIFMD compliance processes</h2>
                    <ComplianceAIForm 
                      onSubmit={handleFormSubmit} 
                      isLoading={isLoading}
                      error={error}
                    />
                  </>
                ) : (
                  <ComplianceAIResponse 
                    response={response} 
                    isLoading={isLoading} 
                    error={error}
                    onReset={() => {
                      setFormData(null);
                      setResponse(null);
                      setError(null);
                    }} 
                  />
                )}
              </div>
            </div>
          </div>
        </section>
        
        {/* How Verdea AI helps section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">How Verdea AI Transforms AIFMD Compliance</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Our platform uses advanced AI to automate and optimize your AIFMD marketing compliance workflow.
                </p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  {
                    title: "AI-Powered Review",
                    description: "Our AI analyzes your marketing materials in seconds, identifying AIFMD compliance issues that would take humans hours to find.",
                    icon: (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-emerald-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    )
                  },
                  {
                    title: "95% Faster Workflow",
                    description: "Reduce your compliance review time by up to 95%, allowing your marketing team to create and publish compliant content faster.",
                    icon: (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-emerald-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    )
                  },
                  {
                    title: "Auto-Documentation",
                    description: "Generate comprehensive compliance documentation automatically, making regulatory inspections and audits much easier to prepare for.",
                    icon: (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-emerald-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    )
                  }
                ].map((feature, index) => (
                  <div
                    key={index}
                    className="bg-white p-6 rounded-xl shadow-md"
                  >
                    {feature.icon}
                    <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        
        {/* Partnership section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Partnering with Leading Financial Firms</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Working together with industry leaders to enhance AIFMD compliance processes.
                </p>
              </div>
              
              <div className="bg-gray-50 p-8 rounded-2xl shadow-md">
                <div className="flex flex-col md:flex-row gap-8 items-center">
                  <div className="md:w-1/3">
                    <img 
                      src="/logos/ejaso.png" 
                      alt="EJASO ETL Global Logo" 
                      className="h-16 mx-auto md:mx-0"
                    />
                  </div>
                  <div className="md:w-2/3">
                    <h3 className="text-xl font-bold text-emerald-600 mb-2">Strategic Compliance Partnership</h3>
                    <p className="text-gray-700 mb-4">
                      Verdea AI works with EJASO ETL Global to develop cutting-edge compliance solutions that streamline 
                      AIFMD workflows, reducing processing time from days to minutes while maintaining exceptional accuracy.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-12 flex flex-wrap gap-8 items-center justify-center">
                <img src="/logos/ant.png" alt="Partner Logo" className="h-8 grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all" />
                <img src="/logos/mm1.png" alt="Partner Logo" className="h-8 grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all" />
                <img src="/logos/vba.png" alt="Partner Logo" className="h-8 grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all" />
                <img src="/logos/greentech.jpg" alt="Partner Logo" className="h-8 grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all" />
              </div>
            </div>
          </div>
        </section>
        
        {/* FAQ section - Concise */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Frequently Asked Questions</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Common questions about our AIFMD compliance tool
                </p>
              </div>
              
              <div className="space-y-4">
                {[
                  {
                    question: "Is this analysis tool completely free?",
                    answer: "Yes, this analysis tool is completely free to use with no registration required. It gives you valuable insights into how AI can improve your AIFMD compliance processes."
                  },
                  {
                    question: "How does Verdea AI handle my data?",
                    answer: "Verdea AI follows strict data protection protocols. The information you provide in the analysis tool is used only to generate your custom recommendations and is not stored or used for other purposes."
                  },
                  {
                    question: "How accurate is Verdea AI for AIFMD compliance?",
                    answer: "Verdea AI achieves a 90% accuracy rate for AIFMD compliance analysis, comparable to expert human reviewers but delivered in a fraction of the time."
                  }
                ].map((faq, index) => (
                  <div
                    key={index}
                    className="bg-white p-6 rounded-xl shadow-sm"
                  >
                    <h3 className="text-xl font-bold mb-2">{faq.question}</h3>
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        
        {/* Final CTA */}
        <section className="py-12 bg-emerald-600 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-4">Ready to see how AI transforms your compliance workflow?</h2>
              <p className="text-xl opacity-90 mb-8 max-w-3xl mx-auto">
                Get your free analysis today and discover how Verdea AI can help your team achieve AIFMD compliance faster and more accurately.
              </p>
              <Button 
                size="lg" 
                className="bg-white text-emerald-600 hover:bg-gray-100 rounded-full px-8"
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Get Your Free Analysis
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      {/* Footer */}
      <footer>
        <Footer />
      </footer>
    </div>
  );
};

export default AIFMDLanding22;